import React from "react";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Pagination,
  Chip,
  useMediaQuery,
  Card,
  CardContent
} from "@mui/material";
import { Visibility } from "@mui/icons-material";

const KycTable = ({
  loading,
  filteredLogs = [],
  kycLogs = [],
  onView,
  currentPage = 1,
  setCurrentPage
}) => {
  const itemsPerPage = 5;
  const [page, setPage] = React.useState(currentPage);
  const isMobile = useMediaQuery("(max-width:768px)");

  React.useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const handlePageChange = (event, value) => {
    setPage(value);
    setCurrentPage && setCurrentPage(value);
  };

  return (
    <Paper sx={{ p: 2, m: isMobile ? 1 : 3 }}>
       {/* HEADER */}
      <Typography variant="h5" fontWeight="bold">
        KYC Logs
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Review your KYC verification history and status updates
      </Typography>

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        All KYC Sessions ({filteredLogs.length} of {kycLogs.length})
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ------------------------------------
              📱 MOBILE VIEW (NO HORIZONTAL SCROLL)
              ------------------------------------ */}
          {isMobile ? (
            <Box>
              {filteredLogs
              .slice((page - 1) * itemsPerPage, page * itemsPerPage)
              .map((log, idx) => (
                <Card
                  key={log.applicant_id || idx}
                  sx={{ mb: 2, borderRadius: 2, boxShadow: 2 }}
                >
                  <CardContent sx={{ p: 2 }}>
                    
                    {/* Applicant ID */}
                    <Typography fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                      {log.applicant_id}
                    </Typography>

                    {/* 🔥 MOBILE TABLE-LIKE STRUCTURE */}
                    <Box sx={{ fontSize: "0.85rem", mt: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <strong>Applicant Name:</strong> <span>{log.user}</span>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <strong>Mobile:</strong> <span>{log.contact}</span>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <strong>Email:</strong> <span>{log.email}</span>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <strong>Auditor Status:</strong>
                        <Chip
                          label={
                            log.rawData?.auditor_status
                              ? log.rawData?.auditor_status.charAt(0).toUpperCase() +
                                log.rawData?.auditor_status.slice(1)
                              : "Pending"
                          }
                          color={
                            log.rawData?.auditor_status === "approved"
                              ? "success"
                              : log.rawData?.auditor_status === "rejected"
                              ? "error"
                              : "warning"
                          }
                          size="small"
                        />
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <strong>VKYC Status:</strong>
                        <Chip
                          label={
                            log.rawData?.kyc_sessions?.[0]?.session_id ||
                            log.kyc_status === "completed"
                              ? "Completed"
                              : "Pending"
                          }
                          color={
                            log.rawData?.kyc_sessions?.[0]?.session_id ||
                            log.kyc_status === "completed"
                              ? "success"
                              : "warning"
                          }
                          size="small"
                        />
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <strong>Onboarding Time:</strong> <span>{log.datetime}</span>
                      </Box>
                    </Box>

                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Visibility />}
                      fullWidth={isMobile} 
                      sx={{ mt: 2, textTransform: "none" }}
                      onClick={() => onView && onView(log)}
                      disabled={!log.rawData?.kyc_sessions?.[0]?.session_id}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            /* ---------------------------
               🖥️ DESKTOP VIEW (TABLE)
               --------------------------- */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Applicant ID</TableCell>
                    <TableCell>Applicant Name</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Auditor Status</TableCell>
                    <TableCell>VKYC Status</TableCell>
                    <TableCell>Onboarding Time</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredLogs
                    .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                    .map((log, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{
                          maxWidth: 140,
                          color: "primary.main",
                          fontWeight: "bold",
                          cursor: "pointer",
                          whiteSpace: "normal",
                          wordBreak: "break-all",
                          lineHeight: 1.2,
                        }}>{log.applicant_id}</TableCell>
                        <TableCell>{log.user}</TableCell>
                        <TableCell>{log.contact}</TableCell>
                        <TableCell>{log.email}</TableCell>

                        <TableCell>
                          <Chip
                            label={
                              log.rawData?.auditor_status
                                ? log.rawData?.auditor_status.charAt(0).toUpperCase() +
                                  log.rawData?.auditor_status.slice(1)
                                : "Pending"
                            }
                            color={
                              log.rawData?.auditor_status === "approved"
                                ? "success"
                                : log.rawData?.auditor_status === "rejected"
                                ? "error"
                                : "warning"
                            }
                            size="small"
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={
                              log.rawData?.kyc_sessions?.[0]?.session_id ||
                              log.kyc_status === "completed"
                                ? "Completed"
                                : "Pending"
                            }
                            color={
                              log.rawData?.kyc_sessions?.[0]?.session_id ||
                              log.kyc_status === "completed"
                                ? "success"
                                : "warning"
                            }
                            size="small"
                          />
                        </TableCell>

                        <TableCell>{log.datetime}</TableCell>

                        <TableCell>
                          <Button
                            variant="outlined"
                            startIcon={<Visibility />}
                            size="small"
                            onClick={() => onView && onView(log)}
                            disabled={!log.rawData?.kyc_sessions?.[0]?.session_id}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={Math.ceil(filteredLogs.length / itemsPerPage)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "large"}
            />
          </Box>
        </>
      )}
    </Paper>
  );
};

export default KycTable;
